import { Avatar, Box, Button, Chip, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import GridViewOutlined from "@mui/icons-material/GridViewOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import React from "react";
import { tokens } from "../../../theme";
import { tints } from "../../../theme/overrides";
import LogoutButton from "./logoutButton";
import { LOGO_HEIGHT, chromeButtonSx, onChrome, type ShellChromeProps } from "./shellChrome";

/**
 * Direction B's shell. Takes exactly the same props as NavRail — <AppShell>
 * picks one from themeConfig.shell and nothing below the shell knows which is
 * mounted. `collapsed` is accepted and ignored: a top bar has nothing to
 * collapse.
 */
const TopBar = (props: ShellChromeProps) => {
  const {
    nav,
    selectedItemId,
    onSelectItem,
    variant,
    onExitSystem,
    contextLabel,
    user,
    onSelectProgram,
    onOpenSystemManagement,
    onLogout
  } = props;

  const items = nav.groups.flatMap((g) => g.items);
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);

  return (
    <Box component="header" sx={{ flex: "none" }}>
      <Box
        sx={{
          height: 56,
          bgcolor: variant === "system" ? tokens.color.railSystemBg : tokens.color.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          gap: 2
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3.25, minWidth: 0 }}>
          <Box
            component="img"
            src={require("../../../assets/logo-mark.png")}
            alt="Zintoro"
            sx={{ height: LOGO_HEIGHT, display: "block", flex: "none" }}
          />

          {variant === "system" ? (
            <Button
              onClick={onExitSystem}
              startIcon={<ChevronLeft />}
              sx={chromeButtonSx}
            >
              {`Back to ${contextLabel ?? "dashboard"}`}
            </Button>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.6,
                minWidth: 0,
                borderRadius: `${tokens.radius.control}px`,
                bgcolor: onChrome.fill
              }}
            >
              <Typography
                sx={{
                  fontSize: tokens.typography.body,
                  fontWeight: 600,
                  color: onChrome.strong,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {contextLabel ?? "Dashboard"}
              </Typography>
              {nav.groups[0] && (
                <Chip
                  size="small"
                  label={nav.groups[0].label}
                  sx={{
                    height: 20,
                    flex: "none",
                    bgcolor: tokens.color.positive.main,
                    color: tokens.color.ink,
                    fontSize: tokens.typography.caption
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "none" }}>
          {nav.otherPrograms.length > 0 && (
            <Tooltip title="Other programs">
              <Button
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                startIcon={<GridViewOutlined />}
                sx={chromeButtonSx}
              >
                Programs
              </Button>
            </Tooltip>
          )}

          {nav.showSystemManagement && variant === "client" && (
            <Tooltip title="System Management">
              <Button
                onClick={onOpenSystemManagement}
                startIcon={<TuneOutlined />}
                sx={chromeButtonSx}
              >
                System
              </Button>
            </Tooltip>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
            <Avatar
              sx={{
                width: 30,
                height: 30,
                bgcolor: tokens.color.positive.main,
                color: tokens.color.ink,
                fontSize: tokens.typography.body
              }}
            >
              {user?.initial ?? ""}
            </Avatar>
            <Box sx={{ minWidth: 0, display: { xs: "none", md: "block" } }}>
              <Typography sx={{ fontSize: tokens.typography.caption, lineHeight: 1.2, color: onChrome.strong }} noWrap>
                {user?.name ?? ""}
              </Typography>
              <Typography
                sx={{ fontSize: 11, lineHeight: 1.2, color: onChrome.muted }}
                noWrap
              >
                {user?.roleLabel ?? ""}
              </Typography>
            </Box>
          </Box>

          <LogoutButton onLogout={onLogout} sx={{ ml: 1 }} />
        </Box>

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          {nav.otherPrograms.map((p) => (
            <MenuItem
              key={p.programIndex}
              onClick={() => {
                setMenuAnchor(null);
                onSelectProgram?.(p.programIndex);
              }}
            >
              {p.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {items.length > 0 && (
        <Box
          sx={{
            height: 52,
            bgcolor: tokens.color.surface,
            borderBottom: `1px solid ${tokens.color.border}`,
            display: "flex",
            alignItems: "center",
            // 20 on the left, not the 24 used everywhere else, because the pill
            // group below adds its own 4px before the first tab. At a flat 24
            // the first LABEL landed at 28 while the logo above and the screen
            // title below both sat at 24 — the tab strip read as indented from
            // the rest of the chrome. 20 + 4 puts the label on the same line as
            // everything else; the group's 4%-alpha background extending 4px
            // further left is not perceptible.
            pl: "20px",
            pr: 3
          }}
        >
          {/* Scrollable past ~6 tabs. If the count keeps growing that is the
              signal to flip to the rail, not to keep shrinking these. */}
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              bgcolor: tints.neutralChip,
              p: 0.5,
              borderRadius: `${tokens.radius.control + 2}px`,
              maxWidth: "100%",
              overflowX: "auto"
            }}
          >
            {items.map((item) => {
              const selected = item.id === selectedItemId;
              return (
                <Box
                  key={item.id}
                  onClick={() => onSelectItem(item.id)}
                  sx={{
                    px: 1.875,
                    py: 0.875,
                    flex: "none",
                    borderRadius: `${tokens.radius.control}px`,
                    fontSize: tokens.typography.body,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontWeight: selected ? 600 : 500,
                    color: selected ? tokens.color.primary : tokens.color.body,
                    bgcolor: selected ? tokens.color.surface : "transparent",
                    boxShadow: selected ? tokens.shadow.card : "none",
                    transition: `background ${tokens.motion.base} ${tokens.motion.easing}, color ${tokens.motion.base}`,
                    "&:hover": selected
                      ? undefined
                      : { bgcolor: tints.primaryChip, color: tokens.color.ink }
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TopBar;
